import React, { ReactNode, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { roundToDigits, shorten } from "@backpacker/number";
import TooltipContent from "@charty/tooltip/serie";
import { FontSize, content, transparentize, useTheme } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { percent } from "@tootools/number";
import Tooltip from "@ui-kit/tooltip";
import { Gauge } from "../model";

export interface IProps {
  gauge: Gauge;
  locale: Locale;
  tooltip?: (gauge: Gauge) => ReactNode;
  total: number;
  onClickGauge?: (uid: Gauge["id"], range?: any, filter?: Gauge["id"]) => void;
  isTooltipDisabled: boolean;
  serie: { id: string | number; label: any; count: number; color?: string };
  localTotal: number;
  isNarrow: boolean;
  isHovered: boolean;
  isFirst: boolean;
  isLast: boolean;
  chartValuesFormat?: "absolute" | "relative";
}

const Indicator = styled.div<{
  color: string;
  onClick?: () => void;
  isFirst: boolean;
  isLast: boolean;
}>`
  position: relative;
  height: 100%;
  cursor: ${(props) =>
    props.onClick ? "var(--cursor-magic-wand)" : "default"};
  background-color: ${(props) => !props.color && content(props, "primary")};
  :hover {
    background-color: ${(props) =>
      !props.color && transparentize(0.6, content(props))};
  }
  border-top-left-radius: ${(props) => props.isFirst && "10px"};
  border-bottom-left-radius: ${(props) => props.isFirst && "10px"};
  border-top-right-radius: ${(props) => props.isLast && "10px"};
  border-bottom-right-radius: ${(props) => props.isLast && "10px"};
`;

const ChartValuesFormat = styled.div`
  position: absolute;
  bottom: 11px;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${FontSize.Small};
  color: ${(props) => content(props, "secondary")};
`;

const getIndicatorMinWidth = (chartValuesFormat, percentage) => {
  if (chartValuesFormat === "absolute") {
    return percentage > 0 && "2px";
  }
  return percentage > 0 && "10px";
};
const GaugeIndicator = ({
  gauge,
  tooltip,
  locale,
  total,
  onClickGauge,
  isTooltipDisabled,
  serie,
  localTotal,
  isNarrow,
  isHovered,
  isFirst,
  isLast,
  chartValuesFormat,
}: IProps) => {
  const OFFSET_WIDTH_LIMIT = 34;
  const [isLargeEnough, setIsLargeEnough] = useState(false);
  const ref = useRef(null);
  const theme = useTheme();
  const percentage = percent(serie.count, localTotal);
  const shortenPercentage = `${shorten(
    locale,
    roundToDigits(percentage, 4),
    3,
    false,
    2
  )}%`;

  useEffect(() => {
    const handleResize = () => {
      if (ref.current) {
        setIsLargeEnough(ref.current.offsetWidth > OFFSET_WIDTH_LIMIT);
      }
    };
    window.addEventListener("resize", handleResize, false);
  }, []);

  useEffect(() => {
    // https://stackoverflow.com/questions/779379/why-is-settimeoutfn-0-sometimes-useful
    setTimeout(() => {
      if (ref.current !== null) {
        setIsLargeEnough(ref.current.offsetWidth > OFFSET_WIDTH_LIMIT);
      }
    }, 0);
  }, [ref, gauge]);
  return (
    <Tooltip
      disabled={isTooltipDisabled}
      tooltip={
        tooltip ? (
          tooltip(gauge)
        ) : (
          <TooltipContent
            label={serie.label}
            color={serie.color}
            count={serie.count}
            locale={locale}
            total={total}
            shorten={false}
            content={
              <>
                {shortenPercentage} -{" "}
                {shorten(locale, serie.count, 3, false, 2)}
              </>
            }
          />
        )
      }
    >
      <Indicator
        ref={ref}
        isFirst={isFirst}
        isLast={isLast}
        color={isHovered ? serie.color : content(theme, "tertiary")}
        style={{
          backgroundColor: isHovered ? serie.color : content(theme, "tertiary"),
          width: percentage + "%",
          minWidth: getIndicatorMinWidth(chartValuesFormat, percentage),
        }}
        onClick={
          onClickGauge ? () => onClickGauge(gauge.id, null, serie.id) : null
        }
      >
        {!isNarrow && isLargeEnough && isHovered && (
          <ChartValuesFormat>
            {chartValuesFormat === "absolute"
              ? shorten(locale, serie.count, 3, false, 2)
              : shortenPercentage}
          </ChartValuesFormat>
        )}
      </Indicator>
    </Tooltip>
  );
};

export default GaugeIndicator;
